// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing } from "@recidiviz/design-system";
import { rem } from "polished";
import styled from "styled-components";

import {
  reviewTypeOptions,
  UsMiSCCReviewV2DraftData,
} from "../../../../WorkflowsStore/Opportunity/Forms/UsMiSCCReviewV2Form";
import {
  FormCheckbox,
  FormDropdown,
  FormInput,
  FormRadioButton,
} from "../SCCReview/FormUtils";

const ContentContainer = styled.div`
  display: grid;
  grid-template: repeat(8, 1fr) / 1fr 2fr 1fr 1fr 1fr;
  height: 120px;
  border: 1px solid black;
  margin-bottom: ${rem(spacing.xs)};
`;

const HeaderRow = styled.div`
  grid-area: 1 / 1 / span 1 / end;
  background-color: #a4cbfa;
  border-bottom: 0.5px solid black;
  padding-left: 2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SegTypeHeading = styled.div`
  grid-area: 6 / 1 / span 1 / span 2;
  padding-left: 2px;
`;
const SegTypeCell = styled.div`
  grid-area: 7 / 1 / span 1 / end;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-bottom: 0.5px solid black;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(spacing.sm)};
  padding: 0 ${rem(spacing.xs)};
`;

const OPTSMICell = styled.div`
  grid-area: 6 / 3 / span 1 / end;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-style: solid;
  border-color: black;
  border-width: 0 0 0.5px 0.5px;
`;

type CellProps = {
  row: number;
  col: number;
  rowSpan?: number | string;
  colSpan?: number | string;
};

const Cell = styled.div<CellProps>`
  grid-area: ${({ row }) => row} / ${({ col }) => col} / span
    ${({ rowSpan }) => rowSpan ?? 1} / span ${({ colSpan }) => colSpan ?? 1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 2px;
  border-style: solid;
  border-color: black;
  border-width: 0 0 ${({ row }) => (row === 8 ? 0 : 0.5)}px
    ${({ col }) => (col === 1 ? 0 : 0.5)}px;
`;

type InputProps = CellProps & {
  title: string;
  name: Extract<keyof UsMiSCCReviewV2DraftData, string>;
  placeholder?: string;
  maxWidth: string;
  colSpan?: number;
};

const InputField = (props: InputProps) => {
  return (
    <>
      <Cell {...props}>{props.title}</Cell>
      <Cell {...props} row={props.row + 1}>
        <FormInput {...props} />
      </Cell>
    </>
  );
};

const segregationClassificationReason = [
  "Pending Misconduct (Bond Revoked)",
  "Inability To Be Managed With GP Privileges",
  "Serious Threat To Physical Safety Of Others",
  "Serious Threat To Good Order Of The Facility",
  "Serious Escape Risk",
  "Under Investigation - Outside Authority - Felony Susp",
  "Control Of Communicable Bloodborne Disease",
];

const FormGeneralInfoV2: React.FC = () => {
  return (
    <ContentContainer>
      <HeaderRow>
        <b>GENERAL INFORMATION</b>
      </HeaderRow>
      <Cell row={2} col={1} colSpan={2} rowSpan={2}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          Review Type:
          <FormDropdown
            name="reviewType"
            menuItems={Object.values(reviewTypeOptions)}
          />
        </div>
      </Cell>
      <InputField row={2} col={3} title="ERD:" name="ERD" maxWidth="80px" />
      <InputField
        row={2}
        col={4}
        colSpan={2}
        title="AMX:"
        name="AMX"
        maxWidth="160px"
      />
      <InputField
        row={4}
        col={1}
        title="Prisoner Number:"
        name="prisonerNumber"
        maxWidth="80px"
      />
      <InputField
        row={4}
        col={2}
        title="Prisoner Name:"
        name="prisonerName"
        maxWidth="160px"
      />
      <InputField
        row={4}
        col={3}
        title="Facility Code:"
        name="facility"
        maxWidth="80px"
      />
      <InputField row={4} col={4} title="Lock:" name="lock" maxWidth="80px" />
      <InputField
        row={4}
        col={5}
        title="Date:"
        name="lockDate"
        maxWidth="80px"
      />
      <Cell row={5} col={5} />
      <SegTypeHeading>
        <b>Type of Segregation:</b> (Check All That Apply):
      </SegTypeHeading>
      <OPTSMICell>
        <CheckboxContainer>
          <div>OPT:</div>
          <div>
            <FormRadioButton name="OPT" targetValue={true} label={"YES"} />
          </div>
          <div>
            <FormRadioButton name="OPT" targetValue={false} label={"NO"} />
          </div>
        </CheckboxContainer>
        <CheckboxContainer>
          <div>SMI:</div>
          <div>
            <FormRadioButton name="SMI" targetValue={true} label={"YES"} />
          </div>
          <div>
            <FormRadioButton name="SMI" targetValue={false} label={"NO"} />
          </div>
        </CheckboxContainer>
      </OPTSMICell>
      <SegTypeCell>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: "8px" }}>
            <FormCheckbox name="adminSeg" label="Administrative" />
          </div>
          Date Classified To:
          <FormInput name="adminSegDate" />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: "8px" }}>
            <FormCheckbox name="punSeg" label="Punitive" />
          </div>
          Date Placed In:
          <FormInput name="punSegDate" />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div style={{ marginRight: "8px" }}>
            <FormCheckbox name="tempSeg" label="Temporary" />
          </div>
          Date Placed In:
          <FormInput name="tempSegDate" />
        </div>
      </SegTypeCell>
      <Cell row={8} col={1} colSpan={"end"}>
        <div style={{ display: "flex", alignItems: "center" }}>
          Reason for Segregation Classification:
          <FormDropdown
            name="segReason"
            menuItems={segregationClassificationReason}
          />
        </div>
      </Cell>
    </ContentContainer>
  );
};

export default FormGeneralInfoV2;
